import { useState } from "react";
import { useThemeContext } from "../../globalContext/global-context";
import type { SurveyAttempt } from "../../session/SurveySession";
import type { HostControlBindings } from "../contracts";
import {
  gridQuestionGroupCore,
  isGridQuestionGroupRaw,
} from "../gridQuestionGroup/core";
import { gridQuestionGroupModule } from "../gridQuestionGroup/module";
import { GridQuestionGroupLayout } from "../gridQuestionGroup/views";
import {
  activeQuestionGroupChild,
  isQuestionGroupContainerRaw,
  questionGroupChildRaw,
  questionGroupCore,
} from "../questionGroup/core";
import { questionGroupModule } from "../questionGroup/module";
import { questionRegistry } from "../registry";
import { groupForValue } from "../visualGroups";
import { ActiveQuestion } from "./ActiveQuestion";
import type { AttemptDraftState, SessionQuestionSlotProps } from "./types";
import { groupDraftValue } from "./util/draftState";
import { prepareGroupAnswer } from "./util/groupSubmission";

export const ActiveQuestionGroup = (
  props: SessionQuestionSlotProps & {
    attempt: SurveyAttempt;
  },
) => {
  const { globalObject } = useThemeContext();
  const [localDraftState, setLocalDraftState] =
    useState<AttemptDraftState | null>(null);
  const raw = props.attempt.question.question;
  /* v8 ignore next -- SessionQuestionSlot dispatches here only for question groups */
  if (!isQuestionGroupContainerRaw(raw)) return null;
  const gridDefinition = isGridQuestionGroupRaw(raw)
    ? gridQuestionGroupCore.decode(raw)
    : null;
  const definition = gridDefinition
    ? gridDefinition
    : questionGroupCore.decode({ ...raw, type: "question_group" });
  const isGrid = gridDefinition !== null;
  const scopeKey = `group:${definition.groupId}`;
  const copy = globalObject.themeData.normalizedTheme.copy;
  const draftState = props.draftState ?? localDraftState;
  /* v8 ignore next -- direct slot consumers may omit the shared draft setter */
  const setDraftState = props.setDraftState ?? setLocalDraftState;

  if (!props.GroupFrame) {
    const child = activeQuestionGroupChild(raw);
    /* v8 ignore next -- malformed active IDs are rejected by the group decoder */
    if (!child) return null;
    const module = questionRegistry.get(child.question.type);
    /* v8 ignore next -- group child modules are checked by supportsSurveyQuestion */
    if (!module)
      throw new Error(
        `Unsupported survey question type: ${child.question.type}`,
      );
    return (
      <ActiveQuestion
        {...props}
        attempt={props.attempt}
        module={module}
        raw={questionGroupChildRaw(child)}
        groupContext={
          child.row === undefined
            ? undefined
            : groupForValue(gridDefinition?.rowGroups, child.row)?.text
        }
        draftId={child.id}
        draftScope={scopeKey}
        answered={child.answered}
        view={undefined}
      />
    );
  }

  const { Frame: GroupFrame, ItemFrame } = props.GroupFrame;
  const childValidity = new Map(
    definition.children.map((child) => {
      if (child.answered || child.question.auto_response === "ok") {
        return [child.id, true] as const;
      }
      const module = questionRegistry.get(child.question.type);
      /* v8 ignore next -- group child modules are checked by supportsSurveyQuestion */
      if (!module) return [child.id, false] as const;
      const childDefinition = module.core.decode(questionGroupChildRaw(child));
      const draft = groupDraftValue(
        draftState,
        props.session,
        scopeKey,
        child,
        module,
        childDefinition,
      );
      return [
        child.id,
        module.core.validate(childDefinition, draft, copy).valid,
      ] as const;
    }),
  );
  const childCompletion = new Map(
    definition.children.map(
      (child) =>
        [
          child.id,
          child.answered ||
            (!child.question.error_message &&
              childValidity.get(child.id) === true),
        ] as const,
    ),
  );
  const pendingChildren = definition.children.filter(
    (child) => !child.answered,
  );
  const allValid = pendingChildren.every(
    (child) => childValidity.get(child.id) === true,
  );
  const snapshot = props.session.getSnapshot();
  const requestedAction = isGrid
    ? gridQuestionGroupModule.controls(
        gridDefinition!,
        gridQuestionGroupCore.initialize(gridDefinition!),
        { view: "default", arrangement: props.arrangement },
        copy,
      ).primaryAction
    : questionGroupModule.controls(
        definition,
        questionGroupCore.initialize(definition),
        { view: "default", arrangement: props.arrangement },
        copy,
      ).primaryAction;
  /* v8 ignore next -- the question-group module contract always declares its group action */
  if (!requestedAction) {
    throw new Error("Question group module requires a primary action.");
  }
  const actionDisabled =
    Boolean(requestedAction.disabled) ||
    !snapshot.canSubmit ||
    !allValid ||
    pendingChildren.length === 0;
  const controls: HostControlBindings = {
    canSubmit: snapshot.canSubmit,
    primaryAction: {
      purpose: requestedAction.purpose,
      label: requestedAction.label,
      disabled: actionDisabled,
      pending: snapshot.pending !== null || Boolean(requestedAction.pending),
      run: () => {
        if (actionDisabled) return;
        props.session.submitGroup(
          props.attempt,
          definition.groupId,
          (question) =>
            prepareGroupAnswer(question, draftState, props.session, copy),
        );
      },
    },
  };
  return (
    <GroupFrame
      attemptId={props.attempt.id}
      heading={definition.heading}
      controls={controls}
      containerType={raw.type}
    >
      {(() => {
        const items = definition.children.map((child) => {
          const module = questionRegistry.get(child.question.type);
          /* v8 ignore next -- group child modules are checked by supportsSurveyQuestion */
          if (!module)
            throw new Error(
              `Unsupported survey question type: ${child.question.type}`,
            );
          return {
            childId: child.id,
            row: child.row ?? "",
            field: child.field,
            complete: childCompletion.get(child.id) === true,
            content: (
              <ActiveQuestion
                {...props}
                key={child.id}
                attempt={props.attempt}
                Frame={ItemFrame}
                module={module}
                raw={questionGroupChildRaw(child)}
                draftId={child.id}
                draftScope={scopeKey}
                answered={child.answered}
                correctionActive={child.id === definition.activeChildId}
                deferSubmission
                draftState={draftState}
                setDraftState={setDraftState}
                promptInHistory={false}
                history={undefined}
                view={undefined}
              />
            ),
          };
        });
        return isGrid ? (
          <GridQuestionGroupLayout
            definition={gridDefinition!}
            arrangement={props.arrangement}
            items={items}
          />
        ) : (
          items.map((item) => item.content)
        );
      })()}
    </GroupFrame>
  );
};
